import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import plugbookClient from '../clients/PlugbookClient'
import { logger } from '../logger'
import { ensurePlugbookRecord } from '../providers/WalletProvider'
import { useWalletProperties } from '../state'
import useDebouncedState from './useDebouncedState'
import useFindTokens from './useFindTokens'

export const useAccountForm = (principalId: string, isNewAccount: boolean) => {
  const { t } = useTranslation()
  const { findTokens } = useFindTokens()
  const { walletNames, walletPlugbookRecords, setWalletPlugbookRecord, setWalletName } =
    useWalletProperties()

  const initialName = walletNames[principalId] || ''
  let initialPlugbook = walletPlugbookRecords[principalId] || { name: '' }

  const [isLoading, setIsLoading] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [plugbookNameValidation, setPlugbookNameValidation] = useState<{
    valid?: boolean
    error?: string | null
  }>()

  const [name, setName] = useState(initialName)
  const [profilePic, setProfilePic] = useState(initialPlugbook.profilePic)
  const [plugbookNameDebounced, setPlugbookName, plugbookName] = useDebouncedState(
    initialPlugbook.name,
    300
  )

  useEffect(() => {
    if (isNewAccount) findTokens(principalId)
  }, [])

  useEffect(() => {
    const fetchPlugbook = async () => {
      try {
        initialPlugbook = await ensurePlugbookRecord(principalId)

        setProfilePic(initialPlugbook.profilePic)
        setPlugbookName(initialPlugbook.name)
      } catch (e) {
        logger.error('Failed to fetch plugbook', { originalError: e })
      }
    }

    fetchPlugbook()
  }, [])

  useEffect(() => {
    setPlugbookNameValidation(undefined)

    if (plugbookNameDebounced !== initialPlugbook.name) {
      plugbookClient
        .validateName(plugbookNameDebounced)
        .then(setPlugbookNameValidation)
        .catch(logger.error)
    }
  }, [plugbookNameDebounced])

  const saveAccount = async () => {
    setIsLoading(true)

    try {
      const plugbook = await plugbookClient.updateNamedAddress(
        { ...initialPlugbook, profilePic, name: plugbookName },
        principalId
      )

      setWalletPlugbookRecord(principalId, plugbook)
      setWalletName(principalId, name)
    } catch (e) {
      logger.error('Failed to update plugbook record', { originalError: e })
      setSaveError((e as Error).message || t('accounts.failedToSave'))
    } finally {
      setIsLoading(false)
    }
  }

  let canSave = isNewAccount || name !== initialName || profilePic !== initialPlugbook.profilePic
  if (plugbookNameDebounced !== initialPlugbook.name) {
    canSave = !!plugbookNameValidation?.valid
  }

  return {
    name,
    setName,
    plugbookName,
    setPlugbookName,
    plugbookNameValidation,
    profilePic,
    setProfilePic,
    saveAccount,
    saveDisabled: !canSave || isLoading,
    isLoading,
    saveError,
    setSaveError,
  }
}
